import {
  BottomSheet,
  CbhIcon,
  LoadingSpinner,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { useParams } from "react-router-dom";

import { IconButton } from "../../../components/IconButton";
import ErrorComponent from "../../components/ErrorComponent";
import { ProfileImage } from "../../components/ProfileImage";
import { useGetFriendsList } from "../../hooks/useGetFriendsList";
import { useGetOutboundFriendsRequests } from "../../hooks/useGetOutboundFriendsRequests";
import { useGetWorkerPublicProfile } from "../../hooks/useGetWorkerPublicProfile";
import { useSendFriendRequest } from "../../hooks/useSendFriendRequest";
import { type FriendIdParams } from "../../paths";
import { formatNameWithLastInitial } from "../../utils/formatNameWithLastInitial";
import WorkWithFriendsContext from "../../WorkWithFriendsContext";

interface Props {
  modalState: UseModalState;
  onClose: () => void;
  friendId?: string;
}

export default function AddFriendBottomSheet(props: Props) {
  const { modalState, onClose } = props;

  const { showSuccessToast, showErrorToast } = useToast();
  const queryClient = useQueryClient();
  const worker = useDefinedWorker();
  const { friendId: parameterFriendId = "" } = useParams<FriendIdParams>();

  const friendId = isDefined(props.friendId) ? props.friendId : parameterFriendId;

  const {
    data: profileData,
    isLoading: isProfileLoading,
    isError: isProfileError,
  } = useGetWorkerPublicProfile(friendId);
  const {
    data: friendsList,
    isLoading: isFriendsLoading,
    isError: isFriendsError,
  } = useGetFriendsList();
  const {
    data: outboundRequests,
    isLoading: isOutboundLoading,
    isError: isOutboundError,
  } = useGetOutboundFriendsRequests();
  const { setSelectedTab } = useContext(WorkWithFriendsContext);

  const { mutate: sendFriendRequest, isLoading: isSending } = useSendFriendRequest({
    onSuccess: () => {
      if (isDefined(profileData)) {
        showSuccessToast(
          `Friend request sent to ${formatNameWithLastInitial(profileData.attributes)}`
        );
        void queryClient.invalidateQueries({ queryKey: ["outbound-friends-requests"] });
        setSelectedTab("pending");
        onClose();
      }
    },
    onError: () => {
      showErrorToast("Something went wrong when sending friend request. Please try again.");
    },
  });

  const isAlreadyFriend = friendsList?.data.some((friend) => friend.id === friendId);

  const hasOutboundRequest = outboundRequests?.data.some((request) => request.id === friendId);

  const isLoading = isProfileLoading || isFriendsLoading || isOutboundLoading;
  const isError = isProfileError || isFriendsError || isOutboundError;

  if (isLoading) {
    return (
      <BottomSheet modalState={modalState}>
        <Stack flex={1} justifyContent="center" alignItems="center" sx={{ padding: 10 }}>
          <LoadingSpinner size="medium" />
        </Stack>
      </BottomSheet>
    );
  }

  return (
    <BottomSheet modalState={modalState}>
      <Stack flex={1} flexDirection="column">
        <Stack flex={1} alignItems="flex-end" p={4}>
          <IconButton
            aria-label="close"
            variant="outlined"
            iconType="close"
            size="small"
            onClick={onClose}
          />
        </Stack>
        <Stack px={6} pb={10} alignItems="center">
          <Stack mt={10} mx={10} alignItems="center">
            {isError || !isDefined(profileData) ? (
              <ErrorComponent />
            ) : (
              <>
                <ProfileImage
                  firstName={profileData.attributes.firstName}
                  lastName={profileData.attributes.lastName}
                  src={profileData.attributes.profileImageUrl}
                  size="large"
                />
                <Title
                  component="h3"
                  variant="h3"
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                  })}
                  py={4}
                  align="center"
                >
                  Add friend
                </Title>
                <Title
                  component="h6"
                  variant="h6"
                  sx={(theme) => ({
                    color: theme.palette.text.secondary,
                  })}
                  align="center"
                >
                  Add {formatNameWithLastInitial(profileData.attributes)} as a friend so they know
                  the shifts you are booking.
                </Title>
              </>
            )}
          </Stack>
          <Stack spacing={4} flex={1} width="100%" mt={10}>
            <LoadingButton
              isLoading={isSending}
              variant="contained"
              disabled={
                worker.userId === friendId ||
                isAlreadyFriend === true ||
                hasOutboundRequest === true ||
                isError
              }
              onClick={() => {
                sendFriendRequest(friendId);
              }}
            >
              <CbhIcon sx={{ mr: 2 }} type="invite" />
              {
                // eslint-disable-next-line no-nested-ternary
                isAlreadyFriend
                  ? "You're already friends!"
                  : hasOutboundRequest
                    ? "Friend request pending"
                    : "Send Friend Request"
              }
            </LoadingButton>
          </Stack>
        </Stack>
      </Stack>
    </BottomSheet>
  );
}
